import { motion } from 'framer-motion';
import { CheckCircle2, Star } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { PageHero } from '@/components/Cards';
import { useLanguage } from '@/hooks/useLanguage';

interface BusinessDetailPageProps {
  title: string;
  titleTh: string;
  subtitle: string;
  subtitleTh: string;
  heroImage: string;
  description: string;
  descriptionTh: string;
  features: { title: string; titleTh: string; desc: string; descTh: string }[];
  images: string[];
  products?: { name: string; nameTh: string; desc: string; descTh: string; image: string }[];
}

function BusinessDetailPage({
  title, titleTh, subtitle, subtitleTh, heroImage,
  description, descriptionTh, features, images, products
}: BusinessDetailPageProps) {
  const { t } = useLanguage();

  return (
    <Layout>
      <PageHero title={title} titleTh={titleTh} subtitle={subtitle} subtitleTh={subtitleTh} image={heroImage} />

      {/* Overview */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl font-bold text-foreground mb-6">{t(title, titleTh)}</h2>
              <p className="text-muted-foreground leading-relaxed text-lg mb-8">{t(description, descriptionTh)}</p>
              <div className="space-y-3">
                {features.map((f) => (
                  <div key={f.title} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-foreground">{t(f.title, f.titleTh)}</span>
                      <span className="text-muted-foreground"> — {t(f.desc, f.descTh)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              {images.slice(0, 4).map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt={title}
                  className={`rounded-2xl object-cover shadow-md w-full ${i === 0 ? 'col-span-2 h-52' : 'h-40'}`}
                />
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      {products && (
        <section className="py-20 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-foreground">{t('Featured Products', 'สินค้าเด่น')}</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {products.map((p, i) => (
                <motion.div
                  key={p.name}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-2xl overflow-hidden shadow-md border border-border/50"
                >
                  <img src={p.image} alt={p.name} className="w-full h-48 object-cover" />
                  <div className="p-5">
                    <div className="flex items-center gap-1 mb-2">
                      {[1,2,3,4,5].map((s) => <Star key={s} className="w-3 h-3 fill-accent text-accent" />)}
                    </div>
                    <h3 className="font-bold text-foreground mb-1">{t(p.name, p.nameTh)}</h3>
                    <p className="text-sm text-muted-foreground">{t(p.desc, p.descTh)}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}

export function MedicalDevices() {
  return (
    <BusinessDetailPage
      title="Medical Devices"
      titleTh="อุปกรณ์การแพทย์"
      subtitle="Professional healthcare equipment for Southeast Asia"
      subtitleTh="อุปกรณ์ดูแลสุขภาพระดับมืออาชีพสำหรับเอเชียตะวันออกเฉียงใต้"
      heroImage="/images/medical_1.jpeg"
      description="Yaruwat distributes a comprehensive range of high-quality medical devices and healthcare equipment sourced from certified global manufacturers. We serve hospitals, clinics, and healthcare facilities across Thailand and Southeast Asia with reliable, CE-marked, and FDA-approved devices."
      descriptionTh="ยารุวาทจัดจำหน่ายอุปกรณ์การแพทย์และเครื่องมือดูแลสุขภาพคุณภาพสูงครบครัน จากผู้ผลิตระดับโลกที่ได้รับการรับรอง เราให้บริการโรงพยาบาล คลินิก และสถานพยาบาลทั่วประเทศไทยและเอเชียตะวันออกเฉียงใต้ ด้วยอุปกรณ์ที่มีเครื่องหมาย CE และได้รับการรับรองจาก FDA"
      features={[
        { title: 'CE & FDA Certified', titleTh: 'ได้รับการรับรอง CE และ FDA', desc: 'All devices meet international safety and quality standards', descTh: 'อุปกรณ์ทั้งหมดตรงตามมาตรฐานความปลอดภัยและคุณภาพสากล' },
        { title: 'Wide Product Range', titleTh: 'ผลิตภัณฑ์หลากหลาย', desc: 'Diagnostic, surgical, monitoring, and rehabilitation equipment', descTh: 'อุปกรณ์วินิจฉัย ผ่าตัด ติดตาม และฟื้นฟูสมรรถภาพ' },
        { title: 'After-Sales Support', titleTh: 'การสนับสนุนหลังการขาย', desc: 'Technical support, maintenance, and spare parts supply', descTh: 'การสนับสนุนทางเทคนิค การบำรุงรักษา และจัดหาอะไหล่' },
        { title: 'Regulatory Compliance', titleTh: 'การปฏิบัติตามข้อกำหนด', desc: 'Full compliance with Thai FDA and regional health regulations', descTh: 'ปฏิบัติตาม อย. ไทยและข้อกำหนดด้านสุขภาพของภูมิภาคอย่างครบถ้วน' },
      ]}
      images={['/images/medical_1.jpeg', '/images/medical_5.jpeg', '/images/medical_4.jpeg', '/images/medical_8.jpeg']}
      products={[
        { name: 'Diagnostic Equipment', nameTh: 'อุปกรณ์วินิจฉัย', desc: 'Advanced imaging and diagnostic devices for accurate medical assessment.', descTh: 'อุปกรณ์ถ่ายภาพและวินิจฉัยขั้นสูงสำหรับการประเมินทางการแพทย์ที่แม่นยำ', image: '/images/medical_8.jpeg' },
        { name: 'Patient Monitoring', nameTh: 'ติดตามผู้ป่วย', desc: 'Real-time monitoring systems for ICU and general wards.', descTh: 'ระบบติดตามแบบเรียลไทม์สำหรับ ICU และวอร์ดทั่วไป', image: '/images/medical_5.jpeg' },
        { name: 'Surgical Instruments', nameTh: 'เครื่องมือผ่าตัด', desc: 'High-precision surgical tools and operating room equipment.', descTh: 'เครื่องมือผ่าตัดความแม่นยำสูงและอุปกรณ์ห้องผ่าตัด', image: '/images/medical_4.jpeg' },
      ]}
    />
  );
}

export function CoffeeProducts() {
  return (
    <BusinessDetailPage
      title="Coffee Products"
      titleTh="ผลิตภัณฑ์กาแฟ"
      subtitle="Premium Thai highland coffee for the world"
      subtitleTh="กาแฟที่ราบสูงไทยพรีเมียมสำหรับโลก"
      heroImage="/images/coffee_4.jpeg"
      description="Yaruwat sources the finest Arabica and Robusta coffee beans from Thailand's northern highlands — including Chiang Mai, Chiang Rai, and Doi Inthanon regions. Our coffee products range from raw green beans to premium roasted blends, instant coffee, and specialty single-origin offerings."
      descriptionTh="ยารุวาทจัดหาเมล็ดกาแฟอาราบิก้าและโรบัสต้าชั้นเลิศจากที่ราบสูงทางภาคเหนือของประเทศไทย รวมถึงเชียงใหม่ เชียงราย และพื้นที่ดอยอินทนนท์ ผลิตภัณฑ์กาแฟของเราครอบคลุมตั้งแต่เมล็ดกาแฟดิบไปจนถึงกาแฟคั่วพรีเมียม กาแฟสำเร็จรูป และกาแฟซิงเกิลออริจินพิเศษ"
      features={[
        { title: 'Highland Arabica', titleTh: 'อาราบิก้าที่ราบสูง', desc: 'Grown at 1,200+ meters altitude for complex, rich flavor profiles', descTh: 'ปลูกที่ระดับความสูง 1,200+ เมตร เพื่อรสชาติที่ซับซ้อนและเข้มข้น' },
        { title: 'Sustainable Sourcing', titleTh: 'การจัดหาที่ยั่งยืน', desc: 'Fair trade partnerships with hill tribe farmers', descTh: 'ความร่วมมือการค้าที่เป็นธรรมกับเกษตรกรบนที่สูง' },
        { title: 'Export Ready', titleTh: 'พร้อมส่งออก', desc: 'Full export documentation and international food safety compliance', descTh: 'เอกสารส่งออกครบถ้วนและการปฏิบัติตามความปลอดภัยอาหารระหว่างประเทศ' },
        { title: 'Custom Blending', titleTh: 'การผสมตามสั่ง', desc: 'OEM and private label services available for brands worldwide', descTh: 'บริการ OEM และ Private Label สำหรับแบรนด์ทั่วโลก' },
      ]}
      images={['/images/coffee_4.jpeg', '/images/coffee_2.jpeg', '/images/coffee_5.jpeg', '/images/coffee_1.jpeg']}
      products={[
        { name: 'Premium Arabica Beans', nameTh: 'เมล็ดอาราบิก้าพรีเมียม', desc: 'Single-origin highland Arabica with notes of chocolate and citrus.', descTh: 'อาราบิก้าที่ราบสูงซิงเกิลออริจินที่มีกลิ่นช็อกโกแลตและส้ม', image: '/images/coffee_7.jpeg' },
        { name: 'Thai Blend Roast', nameTh: 'กาแฟผสมไทยคั่ว', desc: 'Signature Yaruwat blend combining the best of Thai highland varieties.', descTh: 'กาแฟผสมซิกเนเจอร์ยารุวาทที่รวมพันธุ์ที่ราบสูงไทยที่ดีที่สุด', image: '/images/coffee_5.jpeg' },
        { name: 'Specialty Coffee', nameTh: 'กาแฟสเปเชียลตี้', desc: 'Award-winning specialty coffees with unique processing methods.', descTh: 'กาแฟสเปเชียลตี้รางวัลด้วยวิธีการแปรรูปที่เป็นเอกลักษณ์', image: '/images/coffee_1.jpeg' },
      ]}
    />
  );
}

export function ThaiFruits() {
  return (
    <BusinessDetailPage
      title="Thai Fruits Export"
      titleTh="ส่งออกผลไม้ไทย"
      subtitle="GACC-certified fresh tropical fruits to China and beyond"
      subtitleTh="ผลไม้เขตร้อนสดที่ได้รับการรับรอง GACC สู่จีนและทั่วโลก"
      heroImage="/images/fruits_2.jpeg"
      description="Yaruwat is a certified exporter of premium Thai tropical fruits to China and international markets. Our GACC-registered orchards and packing houses ensure the highest quality and compliance standards. Key products include durian, mango, mangosteen, longan, and dragon fruit — all certified and traceable from farm to port."
      descriptionTh="ยารุวาทเป็นผู้ส่งออกผลไม้เขตร้อนไทยพรีเมียมที่ได้รับการรับรองสู่จีนและตลาดสากล สวนผลไม้และโรงคัดบรรจุที่ขึ้นทะเบียน GACC ของเรารับประกันมาตรฐานคุณภาพและการปฏิบัติตามข้อกำหนดสูงสุด ผลิตภัณฑ์หลักได้แก่ ทุเรียน มะม่วง มังคุด ลำไย และมังกรหยก ทั้งหมดได้รับการรับรองและสามารถตรวจสอบย้อนกลับจากฟาร์มถึงท่าเรือ"
      features={[
        { title: 'GACC Registered', titleTh: 'ขึ้นทะเบียน GACC', desc: 'All orchards and packing houses officially registered with Chinese GACC', descTh: 'สวนผลไม้และโรงคัดบรรจุทั้งหมดขึ้นทะเบียนกับ GACC จีนอย่างเป็นทางการ' },
        { title: 'Cold Chain Logistics', titleTh: 'โลจิสติกส์ห่วงโซ่ความเย็น', desc: 'Maintain freshness from orchard to customer with temperature-controlled transport', descTh: 'รักษาความสดจากสวนถึงลูกค้าด้วยการขนส่งที่ควบคุมอุณหภูมิ' },
        { title: 'Phytosanitary Compliance', titleTh: 'การปฏิบัติตามมาตรการสุขอนามัยพืช', desc: 'Full phytosanitary certification for all export shipments', descTh: 'ใบรับรองสุขอนามัยพืชครบถ้วนสำหรับการส่งออกทุกครั้ง' },
        { title: 'Partner: Xianpinda China', titleTh: 'พันธมิตร: Xianpinda ประเทศจีน', desc: 'Strategic distribution partnership in the Chinese market', descTh: 'ความร่วมมือด้านการจัดจำหน่ายเชิงกลยุทธ์ในตลาดจีน' },
      ]}
      images={['/images/fruits_2.jpeg', '/images/fruits_5.jpeg', '/images/fruits_6.jpeg', '/images/fruits_1.jpeg']}
      products={[
        { name: 'Premium Durian', nameTh: 'ทุเรียนพรีเมียม', desc: 'Monthong and Chanee varieties, grade A export quality.', descTh: 'ทุเรียนพันธุ์หมอนทองและชะนี คุณภาพส่งออกเกรด A', image: '/images/fruits_2.jpeg' },
        { name: 'Fresh Mango', nameTh: 'มะม่วงสด', desc: 'Nam Dok Mai and Mahachanok mangoes, premium sweet varieties.', descTh: 'มะม่วงพันธุ์น้ำดอกไม้และมหาชนก พันธุ์หวานพรีเมียม', image: '/images/fruits_5.jpeg' },
        { name: 'Tropical Mix', nameTh: 'ผลไม้เขตร้อนผสม', desc: 'Mangosteen, longan, dragon fruit, and seasonal Thai fruits.', descTh: 'มังคุด ลำไย มังกรหยก และผลไม้ไทยตามฤดูกาล', image: '/images/fruits_1.jpeg' },
      ]}
    />
  );
}

export function AILiveStreaming() {
  return (
    <BusinessDetailPage
      title="AI Live Streaming"
      titleTh="ไลฟ์สดด้วย AI"
      subtitle="Next-generation e-commerce connecting Thailand to China"
      subtitleTh="อีคอมเมิร์ซรุ่นใหม่ เชื่อมต่อประเทศไทยสู่จีน"
      heroImage="/images/ai_live_7.jpeg"
      description="Yaruwat pioneers AI-powered live streaming e-commerce as a bridge between Thai product suppliers and Chinese consumers. Our platform combines real-time translation, AI virtual hosts, intelligent product recommendation, and seamless cross-border payment processing to create engaging live commerce experiences on major Chinese platforms including TikTok (Douyin), Taobao Live, and WeChat."
      descriptionTh="ยารุวาทบุกเบิกอีคอมเมิร์ซไลฟ์สดด้วย AI เป็นสะพานเชื่อมระหว่างซัพพลายเออร์สินค้าไทยและผู้บริโภคชาวจีน แพลตฟอร์มของเราผสมผสานการแปลภาษาแบบเรียลไทม์ พิธีกร AI เสมือน การแนะนำสินค้าอัจฉริยะ และการประมวลผลการชำระเงินข้ามพรมแดนที่ราบรื่น เพื่อสร้างประสบการณ์ไลฟ์คอมเมิร์ซที่น่าสนใจบนแพลตฟอร์มจีนหลัก"
      features={[
        { title: 'AI Virtual Hosts', titleTh: 'พิธีกร AI เสมือน', desc: '24/7 AI-powered digital hosts that speak Chinese fluently', descTh: 'พิธีกรดิจิทัลด้วย AI ที่พูดภาษาจีนได้คล่อง 24/7' },
        { title: 'Real-time Translation', titleTh: 'การแปลแบบเรียลไทม์', desc: 'Instant Thai-Chinese translation for product descriptions and Q&A', descTh: 'การแปลไทย-จีนทันทีสำหรับรายละเอียดสินค้าและถาม-ตอบ' },
        { title: 'Multi-Platform', titleTh: 'หลายแพลตฟอร์ม', desc: 'Simultaneous streaming on Douyin, Taobao Live, WeChat, and more', descTh: 'สตรีมพร้อมกันบน Douyin, Taobao Live, WeChat และอื่นๆ' },
        { title: 'Cross-Border Payments', titleTh: 'การชำระเงินข้ามพรมแดน', desc: 'Seamless WeChat Pay, Alipay, and international payment integration', descTh: 'การผสาน WeChat Pay, Alipay และการชำระเงินระหว่างประเทศที่ราบรื่น' },
      ]}
      images={['/images/ai_live_7.jpeg', '/images/ai_live_2.jpeg', '/images/ai_live_8.jpeg', '/images/ai_live_3.jpeg']}
      products={[
        { name: 'AI Host Solution', nameTh: 'โซลูชันพิธีกร AI', desc: 'Deploy AI digital hosts for your brand on major Chinese platforms.', descTh: 'ใช้งานพิธีกรดิจิทัล AI สำหรับแบรนด์ของคุณบนแพลตฟอร์มจีนหลัก', image: '/images/ai_live_7.jpeg' },
        { name: 'Live Commerce Setup', nameTh: 'การตั้งค่าไลฟ์คอมเมิร์ซ', desc: 'Complete end-to-end live streaming e-commerce infrastructure.', descTh: 'โครงสร้างพื้นฐานอีคอมเมิร์ซไลฟ์สดครบวงจร', image: '/images/ai_live_8.jpeg' },
        { name: 'Analytics Dashboard', nameTh: 'แดชบอร์ดวิเคราะห์', desc: 'Real-time sales analytics and audience engagement tracking.', descTh: 'การวิเคราะห์ยอดขายแบบเรียลไทม์และการติดตามการมีส่วนร่วมของผู้ชม', image: '/images/ai_live_2.jpeg' },
      ]}
    />
  );
}
